/**
 * Maston power and charging module.
 *
 * Desktop keeps every slide in place and cross-fades the active slide's media
 * into the module backdrop while a linear charge bar drives the hand-off.
 * Tablet and mobile hand the same slides to a single Swiper.
 *
 * The charge bar is a CSS animation rather than a JS clock, so freezing it
 * when the stage leaves the viewport (or when the pointer moves over the
 * active card) is a single attribute and the bar can never drift from what
 * is painted. Re-entering the viewport resumes the remaining time.
 */
if (!customElements.get('maston-power-charging')) {
  const AUTOPLAY_QUERY = '(min-width: 1280px)';
  const CAROUSEL_QUERY = '(max-width: 1279px)';
  const TABLET_BREAKPOINT = 768;
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  /**
   * Reads a CSS custom property that resolves to a bare pixel length.
   *
   * @param {Element} element - Element to read the computed value from.
   * @param {string} property - Custom property name.
   * @param {number} fallback - Value used when the property is missing.
   * @returns {number} Resolved pixel value.
   */
  const readPixels = (element, property, fallback) => {
    const raw = window.getComputedStyle(element).getPropertyValue(property);
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  class MastonPowerCharging extends HTMLElement {
    #abortController = null;
    #autoplayAbort = null;
    #carouselAbort = null;
    #stage = null;
    #track = null;
    #captionLive = null;
    #previousButton = null;
    #nextButton = null;
    #autoplayQuery = null;
    #carouselQuery = null;
    #reducedMotionQuery = null;
    #swiper = null;
    #isCarouselActive = false;
    #isAutoplayActive = false;
    #isHovered = false;
    #isInViewport = false;
    #lastPointerTarget = null;
    #viewportObserver = null;
    #chargeGeneration = 0;

    connectedCallback() {
      if (this.#abortController) return;

      this.#stage = this.querySelector('[data-power-stage]');
      this.#track = this.querySelector('[data-power-track]');
      if (!this.#stage || !this.#track || !this.slides.length) return;

      this.#captionLive = this.querySelector('[data-power-caption-live]');
      this.#previousButton = this.querySelector('[data-power-previous]');
      this.#nextButton = this.querySelector('[data-power-next]');

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#autoplayQuery = window.matchMedia(AUTOPLAY_QUERY);
      this.#carouselQuery = window.matchMedia(CAROUSEL_QUERY);
      this.#reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

      this.#autoplayQuery.addEventListener('change', this.#sync, { signal });
      this.#carouselQuery.addEventListener('change', this.#sync, { signal });
      this.#reducedMotionQuery.addEventListener('change', this.#sync, { signal });

      this.#sync();

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
      }
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;

      this.#disableAutoplay();
      this.#destroyCarousel();

      this.#stage = null;
      this.#track = null;
      this.#captionLive = null;
      this.#previousButton = null;
      this.#nextButton = null;
      this.#autoplayQuery = null;
      this.#carouselQuery = null;
      this.#reducedMotionQuery = null;
      this.dataset.powerMode = 'static';
    }

    get slides() {
      return this.#track ? Array.from(this.#track.querySelectorAll('[data-power-slide]')) : [];
    }

    /** The explicit rendered state, never a positional guess. */
    get activeSlide() {
      return this.#track ? this.#track.querySelector('[data-power-slide-active="true"]') : null;
    }

    /**
     * Marks one slide as the active selection and mirrors it onto every surface.
     *
     * @param {Element} slide - Slide element to activate.
     * @param {object} [options] - Behaviour flags.
     * @param {boolean} [options.restartCharge] - Restart the desktop charge bar.
     */
    setActive(slide, { restartCharge = true } = {}) {
      if (!slide) return;

      this.slides.forEach((item) => {
        const isActive = item === slide;
        item.dataset.powerSlideActive = isActive ? 'true' : 'false';
        const trigger = item.querySelector('[data-power-trigger]');
        if (trigger) trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.#renderCaption(slide);

      if (this.#isAutoplayActive && restartCharge) this.#restartCharge();
      if (this.#isAutoplayActive) this.#applyActiveHover();
    }

    /** Moves the selection by `delta`, wrapping at both ends. */
    advance(delta) {
      const slides = this.slides;
      if (!slides.length) return;

      const current = slides.indexOf(this.activeSlide);
      const from = current === -1 ? 0 : current;
      this.setActive(slides[(from + delta + slides.length) % slides.length]);
    }

    /* ------------------------------------------------------------------- */
    /* Mode selection                                                      */
    /* ------------------------------------------------------------------- */

    #sync = () => {
      // Reduced motion drops both enhancements back to the static grid, which
      // shows every slide at once and needs no transition to stay readable.
      const prefersReduced = this.#reducedMotionQuery.matches;

      if (prefersReduced || !this.#carouselQuery.matches) this.#destroyCarousel();
      if (prefersReduced || !this.#autoplayQuery.matches) this.#disableAutoplay();

      if (prefersReduced) {
        this.#applyMode();
        return;
      }

      if (this.#autoplayQuery.matches) this.#enableAutoplay();
      else this.#mountCarousel();
    };

    #applyMode() {
      if (this.#isAutoplayActive) this.dataset.powerMode = 'autoplay';
      else if (this.#isCarouselActive) this.dataset.powerMode = 'carousel';
      else this.dataset.powerMode = 'static';
    }

    /* ------------------------------------------------------------------- */
    /* Carousel (tablet and mobile)                                        */
    /* ------------------------------------------------------------------- */

    #mountCarousel() {
      if (this.#isCarouselActive) return;

      if (typeof window.loadSwiper !== 'function') {
        console.error('[maston-power-charging] Swiper loader is unavailable.');
        return;
      }

      // Layout must already be in carousel mode before Swiper measures slides.
      this.#isCarouselActive = true;
      this.#applyMode();

      window
        .loadSwiper()
        .then((Swiper) => {
          if (!this.isConnected || !this.#isCarouselActive || this.#swiper) return;
          this.#createSwiper(Swiper);
        })
        .catch((error) => {
          // Fall back to the static grid so every slide stays reachable.
          this.#isCarouselActive = false;
          this.#applyMode();
          console.error('[maston-power-charging] Unable to load Swiper.', error);
        });
    }

    #createSwiper(Swiper) {
      const slideRoots = Array.from(this.#track.children);
      if (!slideRoots.length) return;

      this.#carouselAbort = new AbortController();
      const { signal } = this.#carouselAbort;

      this.#stage.classList.add('swiper');
      this.#track.classList.add('swiper-wrapper');
      slideRoots.forEach((slide) => slide.classList.add('swiper-slide'));

      const mobileSpace = readPixels(this, '--mpc-slide-space-mobile', 12);
      const tabletSpace = readPixels(this, '--mpc-slide-space-tablet', 16);

      this.#swiper = new Swiper(this.#stage, {
        slidesPerView: 'auto',
        spaceBetween: mobileSpace,
        centeredSlides: true,
        centeredSlidesBounds: true,
        speed: 450,
        // Release the gesture once it leans vertical so page scrolling stays free.
        touchAngle: 45,
        threshold: 4,
        longSwipesRatio: 0.2,
        resistanceRatio: 0.65,
        watchOverflow: true,
        // This component owns the button labels and the caption live region, so
        // Swiper's own live region would double every announcement.
        a11y: false,
        breakpoints: {
          [TABLET_BREAKPOINT]: {
            spaceBetween: tabletSpace,
            centeredSlides: true,
            centeredSlidesBounds: true
          }
        },
        on: {
          init: (swiper) => this.#syncFromSwiper(swiper),
          slideChange: (swiper) => this.#syncFromSwiper(swiper)
        }
      });

      this.#previousButton?.addEventListener('click', () => this.#swiper?.slidePrev(), { signal });
      this.#nextButton?.addEventListener('click', () => this.#swiper?.slideNext(), { signal });
    }

    #destroyCarousel() {
      if (!this.#isCarouselActive) return;
      this.#isCarouselActive = false;

      this.#carouselAbort?.abort();
      this.#carouselAbort = null;

      if (this.#swiper) {
        this.#swiper.destroy(true, true);
        this.#swiper = null;
      }

      this.#stage?.classList.remove('swiper');
      this.#track?.classList.remove('swiper-wrapper');
      if (this.#track) {
        Array.from(this.#track.children).forEach((slide) => slide.classList.remove('swiper-slide'));
      }

      this.#setControlsDisabled(true);
      this.#applyMode();
    }

    #syncFromSwiper(swiper) {
      const slide = swiper.slides?.[swiper.activeIndex]?.querySelector('[data-power-slide]');
      if (slide) this.setActive(slide, { restartCharge: false });

      if (this.#previousButton) this.#previousButton.disabled = Boolean(swiper.isBeginning);
      if (this.#nextButton) this.#nextButton.disabled = Boolean(swiper.isEnd);
    }

    #setControlsDisabled(disabled) {
      if (this.#previousButton) this.#previousButton.disabled = disabled;
      if (this.#nextButton) this.#nextButton.disabled = disabled;
    }

    /* ------------------------------------------------------------------- */
    /* Autoplay (desktop)                                                  */
    /* ------------------------------------------------------------------- */

    #enableAutoplay() {
      if (this.#isAutoplayActive) return;

      this.#isAutoplayActive = true;
      this.#applyMode();

      this.#autoplayAbort = new AbortController();
      const { signal } = this.#autoplayAbort;

      this.#track.addEventListener('click', this.#handleCardClick, { signal });
      this.#track.addEventListener('animationend', this.#handleChargeEnd, { signal });

      // Freeze only after the pointer actually moves over the active card.
      // `pointerenter` also fires when the stage scrolls under a stationary
      // cursor, which would pause autoplay the moment the module entered view.
      this.#track.addEventListener('pointermove', this.#handleCardPointerMove, { signal });
      this.#track.addEventListener('pointerleave', this.#handleCardPointerLeave, { signal });

      // Seed the documented starting point only when nothing is active yet.
      // Charge does not start until the stage intersects the viewport.
      this.setActive(this.activeSlide || this.slides[0], { restartCharge: false });
      this.#observeViewport();
    }

    #disableAutoplay() {
      if (!this.#isAutoplayActive) return;
      this.#isAutoplayActive = false;

      this.#autoplayAbort?.abort();
      this.#autoplayAbort = null;
      this.#disconnectViewport();

      this.#clearCharge();
      this.#isHovered = false;
      this.#isInViewport = false;
      this.#lastPointerTarget = null;
      delete this.dataset.powerFrozen;
      this.#applyMode();
    }

    /**
     * Starts the charge bar on first entry and resumes remaining time on
     * later entries. Leaving the viewport pauses the CSS animation in place.
     */
    #observeViewport() {
      this.#disconnectViewport();

      if (!('IntersectionObserver' in window) || !this.#stage) {
        this.#isInViewport = true;
        this.#startOrResumeCharge();
        return;
      }

      this.#viewportObserver = new IntersectionObserver(
        ([entry]) => {
          const isInViewport = Boolean(entry?.isIntersecting);
          if (isInViewport === this.#isInViewport) return;

          this.#isInViewport = isInViewport;
          if (isInViewport) this.#startOrResumeCharge();
          else this.#onLeaveViewport();
        },
        { threshold: 0 }
      );
      this.#viewportObserver.observe(this.#stage);
    }

    /** Drop stale hover so a scroll-away cannot keep the bar frozen on return. */
    #onLeaveViewport() {
      this.#lastPointerTarget = null;
      this.#isHovered = false;
      this.#syncFreeze();
    }

    #disconnectViewport() {
      this.#viewportObserver?.disconnect();
      this.#viewportObserver = null;
    }

    /** Starts a new cycle if none is running; otherwise unfreezes remaining time. */
    #startOrResumeCharge() {
      if (!this.#isAutoplayActive) return;

      const fill = this.activeSlide?.querySelector('[data-power-progress]');
      if (!fill?.hasAttribute('data-power-charging')) this.#restartCharge();
      this.#syncFreeze();
    }

    /** Hover and being off-screen both pause the same CSS animation clock. */
    #syncFreeze() {
      const shouldFreeze = !this.#isInViewport || this.#isHovered;
      if (shouldFreeze) this.dataset.powerFrozen = '';
      else delete this.dataset.powerFrozen;
    }

    #restartCharge() {
      this.#clearCharge();
      if (!this.#isInViewport) return;

      this.#chargeGeneration += 1;

      const fill = this.activeSlide?.querySelector('[data-power-progress]');
      if (!fill) return;

      fill.dataset.powerCharge = String(this.#chargeGeneration);

      // Flush the removal so re-adding the attribute restarts the animation
      // instead of being coalesced into a no-op by the style recalculation.
      void fill.offsetWidth;
      fill.setAttribute('data-power-charging', '');
    }

    #clearCharge() {
      this.slides.forEach((slide) => {
        slide.querySelector('[data-power-progress]')?.removeAttribute('data-power-charging');
      });
    }

    #handleCardClick = (event) => {
      const trigger = event.target.closest('[data-power-trigger]');
      if (!trigger || !this.#track.contains(trigger)) return;

      const slide = trigger.closest('[data-power-slide]');
      if (!slide) return;

      // A click is an explicit skip: do not freeze just because the pointer is
      // still sitting on the card that just became active.
      this.#lastPointerTarget = null;
      this.#isHovered = false;
      this.setActive(slide);
    };

    #handleChargeEnd = (event) => {
      if (!event.target.matches('[data-power-progress][data-power-charging]')) return;
      if (event.target.closest('[data-power-slide]') !== this.activeSlide) return;
      // Dropped animations still fire `animationend`; ignore any cycle that is
      // no longer the live countdown.
      if (event.target.dataset.powerCharge !== String(this.#chargeGeneration)) return;

      this.advance(1);
    };

    #handleCardPointerMove = (event) => {
      if (event.pointerType === 'touch') return;
      this.#lastPointerTarget = event.target instanceof Element ? event.target : null;
      this.#applyActiveHover();
    };

    #handleCardPointerLeave = () => {
      this.#lastPointerTarget = null;
      this.#applyActiveHover();
    };

    /** Pause only while the pointer is over the currently active card. */
    #applyActiveHover() {
      const trigger = this.#lastPointerTarget?.closest('[data-power-trigger]');
      const slide = trigger?.closest('[data-power-slide]');
      const isOverActive = Boolean(slide && slide === this.activeSlide);

      if (isOverActive === this.#isHovered) return;
      this.#isHovered = isOverActive;
      this.#syncFreeze();
    };

    /* ------------------------------------------------------------------- */
    /* Shared                                                              */
    /* ------------------------------------------------------------------- */

    /** Mirrors the active slide's copy into the caption shared from tablet up. */
    #renderCaption(slide) {
      if (!this.#captionLive) return;

      const source = slide.querySelector('[data-power-caption-source]');
      if (!source) return;

      this.#captionLive.replaceChildren(
        ...Array.from(source.children).map((node) => node.cloneNode(true))
      );

      // Re-trigger the enter animation by flushing the attribute between frames.
      this.#captionLive.removeAttribute('data-power-caption-enter');
      void this.#captionLive.offsetWidth;
      this.#captionLive.setAttribute('data-power-caption-enter', '');
    }

    #handleBlockSelect = (event) => {
      const selected = event.target;
      if (!(selected instanceof Element) || !this.contains(selected)) return;

      const slide = selected.matches('[data-power-slide]')
        ? selected
        : selected.querySelector('[data-power-slide]');
      if (!slide) return;

      if (this.#swiper) {
        const index = this.#swiper.slides.indexOf(slide.closest('.swiper-slide'));
        if (index > -1) {
          this.#swiper.slideTo(index);
          return;
        }
      }

      this.setActive(slide);
    };
  }

  customElements.define('maston-power-charging', MastonPowerCharging);
}
